import { fabric } from 'fabric';
import { isUndefined } from 'lodash';
import Vec2 from 'vec2';

import {
  COORDS_EPSILON,
  DEFAULT_ZOOM,
  FREE_DRAWER_REAL_TIME_LENGTH_OFFSET,
  MINIMUM_GAP,
  MINIMUM_ZOOM,
  TEN_THOUSAND,
  ZOOM_MARGIN_ADJUSTMENT,
  ZOOM_PCT_CHANGE,
} from '~/core/constants';
import { LineDirection, isRadiusOut } from '~/core/enums';
import { DIRECTION } from '~/core/enums/drawing-types.enum';
import { roundToArrowLengthGranularity } from '~/core/helpers';
import {
  BoundingBox,
  createBoundingBox,
  getAbsoluteCenter,
  getHeight,
  getWidth,
} from '~/core/helpers/BoundingBox.utils';
import {
  addVectors,
  calculateRadiusFromArc,
  findBoundingBox,
  getDistanceBetween,
  getLinesFromPolygonPoints,
  getMidpoint,
  getVectorBetweenPoints,
  lineContainsPoint,
  linesIntersect,
  minDistanceToOtherPolys,
  multiplyVectorByScalar,
  pointsAreEqual,
  polygonCollisions,
} from '~/core/helpers/MathFunctions.utils';
import { SimpleLine } from '~/core/helpers/SimpleLine.utils';
import {
  ArrowInfo,
  CornerInfo,
  DrawingEdge,
  DrawingShape,
  LineSegment,
  Skeleton,
  SkeletonSeg,
} from '~/core/models';
import {
  getArrowShapeFromSegment,
  isCornerRadiusArc,
  isShapeGroup,
} from '~/core/utils';

/**
 * autoPlaceTranslation given a source polygon, find a translation vector that will place it in a non-colliding location
 * @param sourcePoly: an array of points defining the polygon to be translated
 * @param polyArray: an array of polygons (each an array of points) to test for collisions and attempt to avoid
 * @returns Vec2: a vector defining how much to translate the sourcePoly to avoid collisions
 */
export const autoPlaceTranslation = (
  sourcePoly: Vec2[],
  polyArray: Vec2[][],
  ignoreIndex?: number
): Vec2 => {
  // copy the original polygon so we can translate it if necessary
  let testPoly = sourcePoly.map((value) => value);
  let result = new Vec2(0, 0);
  let collisions = polygonCollisions(testPoly, polyArray);

  if (
    !collisions.some((x) => x) &&
    minDistanceToOtherPolys(testPoly, polyArray) > MINIMUM_GAP
  ) {
    return result;
  }

  // bounding boxes for all polygons in the array
  const polyBounds = polyArray.map((value) => findBoundingBox(value));
  // and their center points
  const polyCenters = polyBounds.map(
    (value) => getAbsoluteCenter(value) || new Vec2(0, 0)
  );

  // find the original dirVector which is the sum of the vectors between overlapping polygon centers
  // and the source center
  const sourceBounds = findBoundingBox(sourcePoly) || createBoundingBox();
  const sourceCenter = getAbsoluteCenter(sourceBounds);
  let dirVector = new Vec2(0, 0);

  polyCenters.forEach((bCenter, index) => {
    if (collisions[index]) {
      const aVec = getVectorBetweenPoints(sourceCenter, bCenter);

      dirVector.add(aVec.normalize());
    }
  });

  // if they're directly on top of each other, shift (right, down)
  if (dirVector.length() === 0) {
    dirVector = new Vec2(1, 1);
  }

  const shiftDistance = 10; // should be calculated to minimize attempts
  const scaledDirVector = dirVector.normalize(true).multiply(shiftDistance);

  // console.debug('initial collisions', collisions);
  let attempts = 50;

  while (
    (attempts-- && collisions.some((x) => x)) ||
    minDistanceToOtherPolys(testPoly, polyArray) < MINIMUM_GAP
  ) {
    testPoly = testPoly.map((point) => point.subtract(scaledDirVector, true));
    collisions = polygonCollisions(testPoly, polyArray, ignoreIndex);
    // console.debug('loop', attempts, testPoly);
    // console.debug('loop collisions', collisions);
  }

  // final result is the delta from any two common points of the source polygon and our translated test
  result = getVectorBetweenPoints(testPoly[0], sourcePoly[0]);

  return result;
};

const getUpperLeftLowerRightPoints = (
  o: fabric.Object
): [{ x: number; y: number }, { x: number; y: number }] => {
  const b = o.getBoundingRect(true, true);

  return [
    { x: b.left, y: b.top },
    { x: b.left + b.width, y: b.top + b.height },
  ];
};

type CanvasPoint = { x: number; y: number };
type BoundingBoxPoints = [CanvasPoint, CanvasPoint];

/**
 * Adjust bounding box points for a group based on its center point
 * @param child the child object relative to the group
 * @param groupCenterVec2 the center of a group of fabric objects
 * @returns the bounding box coordinates
 */
const adjustPointsForGroup =
  (groupCenterVec2: Vec2) =>
  (child: fabric.Object): BoundingBoxPoints => {
    const [upperLeft, lowerRight] = getUpperLeftLowerRightPoints(child).map(
      (point) => new Vec2(point.x, point.y).add(groupCenterVec2, true)
    );

    return [
      { x: upperLeft.x, y: upperLeft.y },
      { x: lowerRight.x, y: lowerRight.y },
    ];
  };

/**
 * Process a fabric group object and return bounding box points for its children
 * @param group the group of fabric objects
 * @returns the bounding box points for the fabric group object children without the corner radius arcs
 */
const adjustPointsForNonRadiusArcObjects = (
  group: fabric.Group
): CanvasPoint[] => {
  const groupCenterPoint = group.getCenterPoint();
  const groupCenterVec2 = new Vec2(groupCenterPoint.x, groupCenterPoint.y);

  return group
    .getObjects()
    .filter((child: fabric.Object) => !isCornerRadiusArc(child))
    .flatMap(adjustPointsForGroup(groupCenterVec2));
};

/**
 * Calculates the canvas bounding box coordinates
 * @param canvas the fabric canvas object
 * @returns the canvas bounding box coordinates
 */
export const getCanvasBoundingBox = (canvas?: fabric.Canvas): BoundingBox => {
  const canvasObjects = canvas?.getObjects() ?? [];
  const canvasPoints = canvasObjects
    .flatMap((obj) =>
      // find bounding box points for the given object (or group)
      isShapeGroup(obj)
        ? adjustPointsForNonRadiusArcObjects(obj as fabric.Group)
        : getUpperLeftLowerRightPoints(obj)
    )
    .flat();

  return findBoundingBox(canvasPoints as Vec2[]);
};

/**
 * Zooms and/or pans the canvas to fit all objects.
 * Zooms to the scale to fit the image in the smallest dimension.  Pans to the center of all objects on the canvas
 * @param canvas - the canvas to auto zoom or pan
 * @param zoom - whether to zoom to fit all the elements
 * @param center - whether to center the canvas on the center of all the elements in the canvas
 */
export const autoZoomPanToFit = (
  canvas: fabric.Canvas,
  zoom: boolean,
  center: boolean
) => {
  if (
    isUndefined(canvas) ||
    !canvas ||
    !canvas.getObjects() ||
    (!zoom && !center)
  ) {
    return;
  }
  const boundingBox = getCanvasBoundingBox(canvas);

  if (!boundingBox) {
    return;
  }
  const bWidth = getWidth(boundingBox);
  const bHeight = getHeight(boundingBox);

  if (zoom && bWidth && bHeight) {
    const xRatio = bWidth / canvas.getWidth() / ZOOM_MARGIN_ADJUSTMENT;
    const yRatio = bHeight / canvas.getHeight() / ZOOM_MARGIN_ADJUSTMENT;
    const zoomRatio = Math.max(xRatio, yRatio);
    const zoomLevel = Math.max(MINIMUM_ZOOM, 1 / zoomRatio);

    canvas.setZoom(zoomLevel);
  }
  if (center) {
    const currentZoom = canvas.getZoom();

    canvas.absolutePan({
      x:
        boundingBox.left * currentZoom -
        (canvas.getWidth() - bWidth * currentZoom) / 2,
      y:
        boundingBox.top * currentZoom -
        (canvas.getHeight() - bHeight * currentZoom) / 2,
    });
  }
};

/**
 * Updates the viewportTransform matrix with new zoom and pan values
 * viewportTransform format: [scaleX, skewY, skewX, scaleY, translateX, translateY]
 * @param vpt the viewportTransform array to update
 * @param zoom the new zoom level (scaleX and scaleY)
 * @param pan the new pan values (translateX and translateY)
 */
const updateViewportTransform = (
  vpt: number[] | undefined,
  zoom: number,
  pan: { x: number; y: number }
): void => {
  if (vpt) {
    vpt[0] = zoom; // scaleX
    vpt[3] = zoom; // scaleY
    vpt[4] = pan.x; // translateX
    vpt[5] = pan.y; // translateY
  }
};

/**
 * Shared zoom logic for zoomIn and zoomOut
 * Zooms on a specific point while keeping that point fixed on screen
 * @param canvas the canvas to zoom
 * @param direction direction of the zoom, 1 to zoom in, -1 to zoom out
 * @param center optional point to zoom on (defaults to canvas center)
 */
const zoomAtPoint = (
  canvas: fabric.Canvas,
  direction: number,
  center?: Vec2
): void => {
  if (!canvas) {
    return;
  }

  // Use provided center or default to canvas center (in screen/viewport coordinates)
  const zoomPoint =
    center || new Vec2(canvas.getWidth() / 2, canvas.getHeight() / 2);

  // Get current zoom and pan
  const currentZoom = canvas.getZoom();
  const vpt = canvas.viewportTransform;

  if (!vpt) {
    // If no viewport transform, just set zoom
    const newZoom = zoomByStep(canvas, direction);

    canvas.setZoom(newZoom);
    canvas.renderAll();

    return;
  }

  const currentPan = { x: vpt[4], y: vpt[5] };

  // Calculate the point in canvas coordinates before zoom
  // Formula: canvasPoint = (screenPoint - pan) / zoom
  const canvasPoint = {
    x: (zoomPoint.x - currentPan.x) / currentZoom,
    y: (zoomPoint.y - currentPan.y) / currentZoom,
  };

  // Calculate new zoom
  const newZoom = zoomByStep(canvas, direction);

  // Calculate the new pan to keep zoomPoint fixed
  // After zoom, we want: zoomPoint = canvasPoint * newZoom + newPan
  // So: newPan = zoomPoint - canvasPoint * newZoom
  const newPan = {
    x: zoomPoint.x - canvasPoint.x * newZoom,
    y: zoomPoint.y - canvasPoint.y * newZoom,
  };

  // Update viewportTransform matrix directly to ensure synchronization
  updateViewportTransform(canvas.viewportTransform, newZoom, newPan);

  // Trigger canvas update
  canvas.requestRenderAll();
};

/**
 * Zoom the canvas IN based on the zoomByStep function
 * Zooms on the center of the drawing area without auto-centering
 * @param canvas the canvas to zoom
 * @param center optional point to zoom on (defaults to canvas center)
 */
export const zoomIn = (canvas: fabric.Canvas, center?: Vec2) => {
  zoomAtPoint(canvas, 1, center);
};

/**
 * Zoom the canvas OUT based on the zoomByStep function
 * Zooms on the center of the drawing area without auto-centering
 * @param canvas the canvas to zoom
 * @param center optional point to zoom on (defaults to canvas center)
 */
export const zoomOut = (canvas: fabric.Canvas, center?: Vec2) => {
  zoomAtPoint(canvas, -1, center);
};

/**
 * creates a defined array of steps for zooming based around a mid-point value and stepping up/down by a defined %
 * the array will extend the same number of steps on either side of the middle until the min value is reached
 * Invalid values for min, max, pctChange are sanitized to something mathematically useful
 * NOTE: this used to make the zoom levels deterministic
 * @param min - minimum zoom value (0 < min < middle)
 * @param middle - the midpoint zoom value for the array (0 < min < middle)
 * @param pctChange - how much each step should change (0.1=10%) value (0 < pctChange < 1)
 */
const makeZoomStepArray = (
  min: number,
  middle: number,
  pctChange: number
): Array<number> => {
  const adjMiddle = Math.abs(middle) > 0 ? Math.abs(middle) : 1; // fallback to 1 because, whatever - this should probably error
  const adjPct = pctChange > 0 && pctChange < 1 ? pctChange : 0.2; // default 20%
  const adjMin = min > 0 && min < adjMiddle ? min : adjPct / 2; // fallback is half the % change

  const steps = new Array(1).fill(adjMiddle);

  for (let i = 0; steps[0] > adjMin; i++) {
    steps.splice(0, 0, steps[0] * (1 - adjPct));
    steps.push(steps[steps.length - 1] * (1 + adjPct));
  }

  return steps;
};

// ============================================================================
// ZOOM STEP HELPERS
// ============================================================================

type ZoomStepContext = {
  currentZoom: number;
  currentIndex: number;
  zoomSteps: number[];
};

/**
 * Checks if zoom is at or below minimum level.
 */
const isAtOrBelowMinZoom = ({
  currentIndex,
  currentZoom,
  zoomSteps,
}: ZoomStepContext): boolean =>
  currentIndex === 0 || currentZoom < zoomSteps[0];

/**
 * Checks if zoom is above maximum level.
 */
const isAboveMaxZoom = ({ currentIndex }: ZoomStepContext): boolean =>
  currentIndex === -1;

/**
 * Calculates the next zoom level when zooming out.
 */
const getZoomOutLevel = (ctx: ZoomStepContext): number => {
  if (isAtOrBelowMinZoom(ctx)) {
    return ctx.currentZoom;
  }

  if (isAboveMaxZoom(ctx)) {
    return ctx.zoomSteps[ctx.zoomSteps.length - 1];
  }

  return ctx.zoomSteps[ctx.currentIndex - 1];
};

/**
 * Calculates the next zoom level when zooming in.
 */
const getZoomInLevel = (ctx: ZoomStepContext): number => {
  const { currentZoom, currentIndex, zoomSteps } = ctx;
  const maxIndex = zoomSteps.length - 1;

  if (currentZoom < zoomSteps[0]) {
    return zoomSteps[0];
  }

  if (currentIndex === -1) {
    return currentZoom;
  }

  if (currentIndex === maxIndex && currentZoom <= zoomSteps[maxIndex]) {
    return zoomSteps[maxIndex];
  }

  if (currentZoom < zoomSteps[currentIndex]) {
    return zoomSteps[currentIndex];
  }

  return zoomSteps[currentIndex + 1];
};

/**
 * returns the value to set the zoom (it does not actually zoom the canvas!!!!) to zoom in or out from the current level
 * with "zoom to fit" as an option, the current zoom level may not match one of our defined steps, but this will step to the
 * next closest value
 * NOTE: This relies on a ZOOM_STEPS array (which gets generated every time right now) that includes all the valid steps
 * NOTE: there's a "straight math" version of this commented out that works fine, except it drifts from any defined value
 * @param canvas the canvas to zoom
 * @param direction direction of the zoom, -1 to zoom out, +1 to zoom in
 */
const zoomByStep = (canvas: fabric.Canvas, direction: number): number => {
  if (!canvas) {
    return DEFAULT_ZOOM;
  }

  if (direction === 0) {
    return canvas.getZoom();
  }

  // TODO: make ZOOM_STEPS global instead of rebuilding each time if we're going to use defined steps
  // make the array of zoom values, starting from dEFAULT_ZOOM and working down to MINIMUM_ZOOM
  // this shouldn't be calculated each time in the real app
  const zoomSteps = makeZoomStepArray(
    MINIMUM_ZOOM,
    DEFAULT_ZOOM,
    ZOOM_PCT_CHANGE
  );

  const currentZoom = canvas.getZoom();
  const currentIndex = zoomSteps.findIndex((value) => value >= currentZoom);

  const ctx: ZoomStepContext = { currentZoom, currentIndex, zoomSteps };

  if (direction < 0) {
    return getZoomOutLevel(ctx);
  }

  if (direction > 0) {
    return getZoomInLevel(ctx);
  }

  return currentZoom;
};

// ============================================================================
// SKELETON CLEANING HELPERS
// ============================================================================

/**
 * Checks if a skeleton point should be kept during deduplication.
 * Removes consecutive duplicates and duplicate last point (closed polygon).
 */
const shouldKeepSkeletonPoint = (
  point: SkeletonSeg,
  index: number,
  skeleton: Skeleton
): boolean => {
  if (index === 0) {
    return true;
  }

  const isLastPoint = index === skeleton.length - 1;
  const isDuplicateOfFirst = pointsAreEqual(skeleton[0].pos, point.pos);

  if (isLastPoint && isDuplicateOfFirst) {
    return false;
  }

  const isDuplicateOfPrevious = pointsAreEqual(
    skeleton[index - 1].pos,
    point.pos
  );

  return !isDuplicateOfPrevious;
};

/**
 * Removes consecutive duplicate points from skeleton.
 */
const removeDuplicatePoints = (sk: Skeleton): Skeleton =>
  sk
    .filter(shouldKeepSkeletonPoint)
    .map((bone) => ({ ...bone, pos: new Vec2(bone.pos.x, bone.pos.y) }));

/**
 * Checks if three points are collinear on a horizontal or vertical line.
 */
const arePointsCollinear = (a: Vec2, b: Vec2, c: Vec2): boolean => {
  const isHorizontalLine = a.y === b.y && b.y === c.y;
  const isVerticalLine = a.x === b.x && b.x === c.x;

  if (!isHorizontalLine && !isVerticalLine) {
    return false;
  }

  return lineContainsPoint(new SimpleLine(a, c), b, true, true);
};

/**
 * Finds indices of middle points that are collinear with their neighbors.
 */
const findCollinearMiddlePoints = (deDupe: Skeleton): number[] => {
  const removals: number[] = [];

  for (let i = 0; i < deDupe.length - 2; i++) {
    const [a, b, c] = [deDupe[i].pos, deDupe[i + 1].pos, deDupe[i + 2].pos];

    if (arePointsCollinear(a, b, c) && !removals.includes(i + 1)) {
      removals.push(i + 1);
    }
  }

  return removals;
};

export const cleanSkeletonPoints = (sk: Skeleton): Skeleton | undefined => {
  let deDupe = removeDuplicatePoints(sk);

  while (deDupe) {
    // started empty or reduced to a single point, it's an invalid skeleton
    if (!deDupe || deDupe.length === 1) {
      return undefined;
    }
    // if < 3 points, no further optimization can be done
    if (deDupe.length < 3) {
      return deDupe;
    }

    const removals = findCollinearMiddlePoints(deDupe);

    if (removals.length === 0) {
      return deDupe;
    }

    deDupe = deDupe.filter((_value, index) => !removals.includes(index));
  }

  return undefined;
};

// ============================================================================
// OUTLINE POINT CALCULATION HELPERS
// ============================================================================

type OutlinePoint = Partial<CornerInfo>;

/**
 * Calculates perpendicular offset points for a given base point.
 */
const calculatePerpendicularPoints = (
  basePoint: Vec2,
  normalVec: Vec2,
  depth: number
): { U: Vec2; V: Vec2 } => ({
  U: basePoint.add(normalVec.multiply(depth, true), true),
  V: basePoint.subtract(normalVec.multiply(depth, true), true),
});

/**
 * Creates outline point objects for the starting segment.
 */
const createStartingOutlinePoints = (
  startPt: Vec2,
  normalVec: Vec2,
  dPrev: number,
  skIndex: number
): OutlinePoint[] => {
  const { U, V } = calculatePerpendicularPoints(startPt, normalVec, dPrev);

  return [
    { coords: U, skIndex, isUpper: true, angle: undefined },
    { coords: V, skIndex, isUpper: false, angle: undefined },
  ];
};

/**
 * Calculates corner offset for middle segments with angle compensation.
 */
const calculateCornerOffset = (
  sk: Skeleton,
  i: number,
  endPt: Vec2,
  diffVec: Vec2,
  nextDiffVec: Vec2,
  dPrev: number,
  dNext: number
): { U: Vec2; V: Vec2; angle: number | undefined } => {
  const angle = diffVec.multiply(-1, true).angleTo(nextDiffVec);

  if (angle === undefined || !angle) {
    console.warn('Undefined angle:', sk[i], sk[i + 1], diffVec, nextDiffVec);
  }

  const alpha = Math.atan(
    (dPrev * Math.sin(angle)) / (dNext + dPrev * Math.cos(angle))
  );

  const diffVecLength = Math.sqrt(
    diffVec.x * diffVec.x + diffVec.y * diffVec.y
  );
  const normalizedDiffVec = new Vec2(
    diffVecLength > 0 ? diffVec.x / diffVecLength : 0,
    diffVecLength > 0 ? diffVec.y / diffVecLength : 0
  );

  const cosAlpha = Math.cos(alpha);
  const sinAlpha = Math.sin(alpha);
  const dirVec = new Vec2(
    normalizedDiffVec.x * cosAlpha - normalizedDiffVec.y * sinAlpha,
    normalizedDiffVec.x * sinAlpha + normalizedDiffVec.y * cosAlpha
  );

  const length = alpha === 0 ? dPrev : dPrev / Math.sin(alpha);

  return {
    U: endPt.add(dirVec.multiply(length, true), true),
    V: endPt.subtract(dirVec.multiply(length, true), true),
    angle,
  };
};

/**
 * Calculates end segment points (simple perpendicular).
 */
const calculateEndSegmentPoints = (
  endPt: Vec2,
  normalVec: Vec2,
  dPrev: number
): { U: Vec2; V: Vec2; angle: undefined } => ({
  ...calculatePerpendicularPoints(endPt, normalVec, dPrev),
  angle: undefined,
});

/**
 * Calculates middle segment points with angle compensation.
 */
const calculateMiddleSegmentPoints = (
  sk: Skeleton,
  i: number,
  endPt: Vec2,
  diffVec: Vec2,
  dPrev: number
): { U: Vec2; V: Vec2; angle: number | undefined } => {
  const nextPt = new Vec2(sk[i + 1].pos.x, sk[i + 1].pos.y);
  const nextDiffVec = new Vec2(nextPt.x - endPt.x, nextPt.y - endPt.y);
  const dNext = sk[i].depth;

  return calculateCornerOffset(
    sk,
    i,
    endPt,
    diffVec,
    nextDiffVec,
    dPrev,
    dNext
  );
};

export const getOutlinePts = (
  sk: Skeleton
): Array<Partial<CornerInfo>> | undefined => {
  if (!sk || sk.length < 2) {
    return undefined;
  }

  const points: OutlinePoint[] = [];

  for (let i = 1; i < sk.length; i++) {
    const startPt = new Vec2(sk[i - 1].pos.x, sk[i - 1].pos.y);
    const endPt = new Vec2(sk[i].pos.x, sk[i].pos.y);

    const diffVec = new Vec2(endPt.x - startPt.x, endPt.y - startPt.y);
    const normalVec = diffVec
      .normalize(true)
      .rotate(Math.PI / 2, undefined, true);
    const dPrev = sk[i - 1].depth;

    // Add starting points on first iteration
    if (i === 1) {
      const startingPoints = createStartingOutlinePoints(
        startPt,
        normalVec,
        dPrev,
        i - 1
      );

      points.push(...startingPoints);
    }

    // Calculate U/V points based on segment position
    const isLastSegment = i === sk.length - 1;
    const { U, V, angle } = isLastSegment
      ? calculateEndSegmentPoints(endPt, normalVec, dPrev)
      : calculateMiddleSegmentPoints(sk, i, endPt, diffVec, dPrev);

    // Insert points at middle of array (maintains upper/lower ordering)
    points.splice(
      points.length / 2,
      0,
      { coords: U, skIndex: i, isUpper: true, angle },
      { coords: V, skIndex: i, isUpper: false, angle }
    );
  }

  return points;
};

export const getRectangleSideItemArrowsFromOutlines = (
  points: Vec2[],
  label?: string
): ArrowInfo[] => {
  const arrows = [];

  const newPoints = points.slice(0);

  newPoints.push(points[0]);

  const ltPoint = 0;
  const rtPoint = 1;
  const rbPoint = 2;
  const lbPoint = 3;

  for (let i = 1; i < newPoints.length; i++) {
    const startPt = newPoints[i - 1];
    const endPt = newPoints[i];

    if (startPt !== newPoints[rbPoint] && endPt !== newPoints[lbPoint]) {
      let backsplashLetter = '';
      const arrowShapePoints = getArrowShapeFromSegment(startPt, endPt);

      if (startPt === newPoints[ltPoint] && endPt === newPoints[rtPoint]) {
        backsplashLetter = label ?? '';
      }
      arrows.push({ path: arrowShapePoints, label: backsplashLetter });
    }
  }

  return arrows;
};

export const getBumpOutArrowsFromOutlines = (points: Vec2[]): ArrowInfo[] => {
  const arrows = [];

  const newPoints = points.slice(0);

  newPoints.push(points[0]);

  for (let i = 1; i < newPoints.length; i += 2) {
    const arrowShapePoints = getArrowShapeFromSegment(
      newPoints[i - 1],
      newPoints[i]
    );

    arrows.push({ path: arrowShapePoints, label: '' });
  }

  return arrows;
};

export const getDirection = (line: LineSegment): string => {
  const diffVec = new Vec2(
    line.endPoint.x - line.startPoint.x,
    line.endPoint.y - line.startPoint.y
  );

  let direction = LineDirection.TOP;

  if (Math.abs(diffVec.y) < Math.abs(diffVec.x) * 0.0875) {
    //Above condition is for checking which direction current edge is laid towards, more likely towards the xAxis or yAxis
    //With this information, we will put measurement text horizontally or vertically
    // In other words, the case for angle between the diff vector and x axis is less than 5'
    if (diffVec.x > 0) {
      direction = LineDirection.BOTTOM;
    } else {
      direction = LineDirection.TOP;
    }
  } else if (diffVec.y < 0) {
    if (Math.abs(diffVec.x) < COORDS_EPSILON) {
      direction = LineDirection.VERTICAL_RIGHT;
    } else {
      direction = LineDirection.RIGHT;
    }
  } else if (Math.abs(diffVec.x) < COORDS_EPSILON) {
    direction = LineDirection.VERTICAL_LEFT;
  } else {
    direction = LineDirection.LEFT;
  }

  return direction;
};

export const getOutlinePointsFromShape = (
  dShape: DrawingShape
): Array<Vec2> => {
  if (!dShape?.cornerMap) {
    return [];
  }

  return Object.values(dShape.cornerMap).map((corner) => corner.coords);
};

const addCustomEdgeVirtualCorner = (
  cornerIn: Vec2,
  edge: DrawingEdge
): Vec2 | undefined => {
  const addDepth = isRadiusOut(edge) ? edge.radiusSagitta : edge.bumpOut?.depth;

  return addDepth
    ? cornerIn.add(edge.normalVec.multiply(addDepth, true), true)
    : undefined;
};

const getBigArcPoints = (
  edge: DrawingEdge,
  usePrevEdge: boolean
): [Vec2, Vec2] | undefined => {
  if (!isRadiusOut(edge)) {
    return undefined;
  }

  const chordLength = getDistanceBetween(
    edge.startDisplayVec,
    edge.endDisplayVec
  );
  const radiusSagitta = edge.radiusSagitta as number;
  const bigArc = radiusSagitta > chordLength / 2;

  if (!bigArc) {
    return undefined;
  }

  const changeSign = usePrevEdge ? 1 : -1;
  const radius = calculateRadiusFromArc(chordLength, radiusSagitta);
  const chordMidPoint = getMidpoint(edge.startDisplayVec, edge.endDisplayVec);
  const bigArcPoint0 = chordMidPoint.add(
    edge.dirVec.normalize(true).multiply(radius * changeSign, true),
    true
  );
  const bigArcPoint1 = bigArcPoint0.add(
    edge.normalVec.multiply(radiusSagitta, true),
    true
  );

  return [bigArcPoint0, bigArcPoint1];
};

const getEdgePts = (dir: 'prev' | 'next', vec: Vec2, edge?: DrawingEdge) => {
  if (edge?.customSideType === undefined) {
    return [];
  }

  const bigArcPoints = getBigArcPoints(edge, dir === 'prev');
  const handle = {
    prev: () => [
      addCustomEdgeVirtualCorner(vec, edge),
      ...(bigArcPoints ? [bigArcPoints[1], bigArcPoints[0]] : []),
    ],
    next: () => [
      ...(bigArcPoints ? [bigArcPoints[0], bigArcPoints[1]] : []),
      addCustomEdgeVirtualCorner(vec, edge),
    ],
  };

  return handle[dir]?.() ?? [];
};

// ============================================================================
// OUTLINE POINTS FROM MODIFIED SHAPE HELPERS
// ============================================================================

/**
 * Gets outline points for arrow rendering mode.
 * Uses replacement coordinates for visible diagonals.
 */
const getArrowOutlinePoints = (cornerMap: Record<string, CornerInfo>): Vec2[] =>
  Object.values(cornerMap).flatMap((corner) =>
    corner.diagonalLength &&
    corner.isVisibleInCanvas &&
    corner.replacementStartCoords &&
    corner.replacementEndCoords
      ? [corner.replacementStartCoords, corner.replacementEndCoords]
      : [corner.coords]
  );

/**
 * Checks if corner has valid length for applying modifications.
 */
const hasValidCornerLength = (corner: CornerInfo): boolean =>
  !!(corner.diagonalLength || (corner.size ?? 0) > 0.75) &&
  !!corner.isVisibleInCanvas;

/**
 * Selects appropriate coordinates based on direction and validity.
 */
const selectDirectionalCoords = (
  corner: CornerInfo,
  hasValidLength: boolean,
  direction: 'prev' | 'next'
): Vec2 => {
  if (!hasValidLength) {
    return corner.coords;
  }

  return direction === 'prev'
    ? (corner.replacementStartCoords as Vec2)
    : (corner.replacementEndCoords as Vec2);
};

/**
 * Collects edge points for a corner from drawing edges.
 */
const collectEdgePointsForCorner = (
  corner: CornerInfo,
  edges: DrawingEdge[],
  prevCoords: Vec2,
  nextCoords: Vec2
): { prevPts: (Vec2 | undefined)[]; nextPts: (Vec2 | undefined)[] } => {
  type EdgePointPair = [(Vec2 | undefined)[], (Vec2 | undefined)[]];

  const [prevPts, nextPts] = edges.reduce<EdgePointPair>(
    ([prev, next], edge) => {
      if (edge.endCornerId === corner.id) {
        return [getEdgePts('prev', prevCoords, edge), next];
      }

      if (edge.startCornerId === corner.id) {
        return [prev, getEdgePts('next', nextCoords, edge)];
      }

      return [prev, next];
    },
    [[], []]
  );

  return { prevPts, nextPts };
};

/**
 * Gets regular outline points for a corner based on angle.
 */
const getRegularCornerPoints = (
  corner: CornerInfo,
  hasValidLength: boolean
): (Vec2 | undefined)[] => {
  const hasNegativeAngle = hasValidLength && (corner.angle ?? 0) < 0;

  return hasNegativeAngle
    ? [corner.replacementStartCoords, corner.replacementEndCoords]
    : [corner.coords];
};

/**
 * Conditionals for arrows
 * * If edge before this point is a radius out, then
 *   * add a point using normVec on that edge
 *     * unless this is an end replacement point, then don't
 * * If edge after this point is a radius out, then
 *   * add a point using normVec on that edge
 *     * unless this is a start replacement point, then don't
 * * If neither of the above is true, then
 *   * just add the regular point, or replacement points as
 */
export const getOutlinePointsFromModifiedShape = (
  drawingShape: DrawingShape,
  forArrows = false
): Vec2[] => {
  if (forArrows) {
    return getArrowOutlinePoints(drawingShape.cornerMap);
  }

  return Object.values(drawingShape.cornerMap)
    .flatMap((corner) => {
      const validLength = hasValidCornerLength(corner);

      const prevCoords = selectDirectionalCoords(corner, validLength, 'prev');
      const nextCoords = selectDirectionalCoords(corner, validLength, 'next');

      const { prevPts, nextPts } = collectEdgePointsForCorner(
        corner,
        drawingShape.drawingEdges,
        prevCoords,
        nextCoords
      );

      const regularPts = getRegularCornerPoints(corner, validLength);

      return [...prevPts, ...regularPts, ...nextPts];
    })
    .filter(Boolean) as Vec2[];
};

/**
 * Returns a list with the id's of the corners that have a diagonal applied
 * @param drawingShape
 * @returns the list of corners that have a diagonal applied
 */
export const getOutlineDiagonalFlagsFromModifiedShape = (
  dShape: DrawingShape
) =>
  Object.values(dShape.cornerMap).reduce<{
    diagonals: boolean[];
    cornerIds: (CornerInfo['id'] | undefined)[];
  }>(
    (acc, corner) =>
      corner.diagonalLength && corner.isVisibleInCanvas
        ? {
            diagonals: [...acc.diagonals, false, true],
            cornerIds: [...acc.cornerIds, corner.id, corner.id],
          }
        : {
            diagonals: [...acc.diagonals, false],
            cornerIds: [...acc.cornerIds, undefined],
          },
    { diagonals: [], cornerIds: [] }
  );

/**
 * @param drawingShape
 * @returns the list of outline edges where a custom side was applied (bump out or radius out)
 */
export const getOutlineCustomSideEdges = (
  drawingShape: DrawingShape
): (DrawingEdge | undefined)[] =>
  Object.values(drawingShape.cornerMap).flatMap((corner) => {
    const prevEdge = drawingShape.drawingEdges.find(
      (edge) => edge.startCornerId === corner.id
    );
    const customSide =
      prevEdge?.customSideType !== undefined ? prevEdge : undefined;

    return corner.diagonalLength && corner.isVisibleInCanvas
      ? [undefined, customSide]
      : [customSide];
  });

export const getRefinedEdgePoints = (
  [startX, startY, endX, endY]: Array<number>,
  stroke?: number
) => {
  const offset = stroke ? stroke * 0.5 : 0;

  return [startX - offset, startY - offset, endX - offset, endY - offset];
};

export const freeDrawerRealTimeLength = (
  points: Array<Vec2>
): { length: number; pos: Vec2 } => {
  let length = 0.0;
  let dirVec = new Vec2(0, 0);

  for (let i = 1; i < points.length; i++) {
    const diff = new Vec2(
      points[i].x - points[i - 1].x,
      points[i].y - points[i - 1].y
    );
    const d = diff.length();

    length += d;
    if (i === points.length - 1) {
      const diffLength = diff.length();

      dirVec = new Vec2(
        diffLength > 0 ? diff.x / diffLength : 0,
        diffLength > 0 ? diff.y / diffLength : 0
      );
    }
  }

  const roundedValue = roundToArrowLengthGranularity(length);
  const newPos = addVectors(
    points[points.length - 1],
    multiplyVectorByScalar(dirVec, FREE_DRAWER_REAL_TIME_LENGTH_OFFSET)
  );

  return { length: roundedValue, pos: newPos };
};

export const generateSkeletonData = (
  points: Array<Vec2>,
  depth: number
): Skeleton =>
  points.map(
    (pt: Vec2) =>
      ({
        pos: new Vec2(pt.x, pt.y),
        depth,
      }) as SkeletonSeg
  );

// Get upper, bottom, left, right direction vector for given vector
export const isCorrectDirection = (vec: Vec2, direction: string): boolean => {
  let result = true;
  let dirVec = new Vec2(1, 0);

  switch (direction) {
    case DIRECTION.UP:
      dirVec = new Vec2(0, -1);
      break;
    case DIRECTION.DOWN:
      dirVec = new Vec2(0, 1);
      break;
    case DIRECTION.LEFT:
      dirVec = new Vec2(-1, 0);
      break;
    case DIRECTION.RIGHT:
      dirVec = new Vec2(1, 0);
      break;
  }

  if (vec.x * dirVec.x + vec.y * dirVec.y <= 0) {
    result = false;
  }

  return result;
};

/**
 * Checks whether @param testPoint is inside @param polygon
 * or if it's part of its edges
 */
export const isPointInsidePolygon = (
  polygon: Vec2[],
  testPoint: Vec2
): boolean => {
  const targetLines = getLinesFromPolygonPoints(polygon);
  const sourceLine = new SimpleLine(
    testPoint,
    new Vec2(testPoint.x, TEN_THOUSAND)
  );

  const isPointPartOfThePolygon = targetLines?.some((targetLine) =>
    new Vec2(testPoint)?.isPointOnLine(targetLine.point1, targetLine.point2)
  );

  const intersectionCount = targetLines?.reduce(
    (numberOfIntersections, targetLine) =>
      numberOfIntersections + (linesIntersect(sourceLine, targetLine) ? 1 : 0),
    0
  );

  // if an even number of intersections are found, the point is not inside the polygon
  return !(intersectionCount % 2 === 0) || isPointPartOfThePolygon;
};
